<template>
  <div>
    <v-btn
      v-if="stripeAvailable"
      color="primary"
      outlined
      block
      @click="openDialog"
    >
      <v-icon left>mdi-alpha-s-circle-outline</v-icon>
      {{ $t('stripe_payment.pay_with_stripe') }}
    </v-btn>

    <v-dialog v-model="showDialog" :retain-focus="false" persistent max-width="520px">
      <v-card>
        <v-card-title>{{ $t('stripe_payment.dialog_title') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="paymentAmount"
            :label="$t('stripe_payment.amount')"
            type="number"
            min="0"
            step="0.01"
            outlined
            :disabled="isLoading || paymentStatus === 'pending'"
          ></v-text-field>
          
          <v-textarea
            v-model="paymentDescription"
            :label="$t('stripe_payment.description_field')"
            rows="1"
            auto-grow
            outlined
            :disabled="isLoading || paymentStatus === 'pending'"
          ></v-textarea>
          
          <v-text-field
            v-if="paymentUrl"
            :value="paymentUrl"
            :label="$t('stripe_payment.payment_link_label')"
            readonly
            outlined
            dense
            class="mt-2"
            append-icon="mdi-content-copy"
            @click:append="copyToClipboard(paymentUrl)"
          ></v-text-field>
          
          <div v-if="paymentUrl && qrCode" class="text-center mt-4 mb-2">
            <v-card outlined>
              <v-card-text>
                <div class="text-subtitle-2 mb-2">{{ $t('stripe_payment.qr_code_hint') }}</div>
                <img :src="qrCode" alt="QR Code" style="max-width: 250px; width: 100%;" />
              </v-card-text>
            </v-card>
          </div>
          
          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-2">
            {{ errorMessage }}
          </v-alert>
          
          <div v-if="paymentUrl" class="mt-3 text-center">
            <v-btn small text color="primary" @click="openExternalLink(paymentUrl)">
              <v-icon left small>mdi-open-in-new</v-icon>
              {{ $t('stripe_payment.open_link') }}
            </v-btn>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-btn text @click="closeDialog">{{ $t('custom.close') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="createPayment"
            :loading="isLoading"
            :disabled="isLoading || paymentStatus === 'pending' || !!paymentUrl"
          >
            {{ $t('stripe_payment.create_payment') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import { copyToClipboard as copyTextToClipboard } from '@/utils/clipboard';

export default {
  name: 'StripePaymentDialogPublic',
  props: {
    client: { type: Object, required: true },
    company: { type: Object, required: true },
    currency: { type: String, default: 'RUB' },
    bookingId: { type: [Number, String], required: true },
    lastName: { type: String, required: true }
  },
  data() {
    return {
      showDialog: false,
      paymentAmount: null,
      paymentDescription: '',
      paymentUrl: null,
      qrCode: null,
      isLoading: false,
      paymentStatus: null,
      paymentId: null,
      orderId: null,
      errorMessage: null,
    };
  },
  computed: {
    stripeAvailable() {
      return this.company?.stripe_enabled && this.company?.stripe_publishable_key && this.company?.stripe_secret_key;
    },
  },
  methods: {
    openDialog() {
      if (!this.stripeAvailable) {
        this.$notify(
          this.$t('stripe_payment.not_configured_title'),
          this.$t('stripe_payment.not_configured_text'),
          'warning',
          4000
        );
        return;
      }
      this.resetDialog();
      this.showDialog = true;
    },
    
    closeDialog() {
      this.showDialog = false;
      this.resetDialog();
    },
    
    resetDialog() {
      this.paymentAmount = null;
      this.paymentDescription = '';
      this.paymentUrl = null;
      this.qrCode = null;
      this.paymentStatus = null;
      this.paymentId = null;
      this.orderId = null;
      this.errorMessage = null;
    },
    
    createPayment() {
      const amount = parseFloat((this.paymentAmount || '').toString().replace(',', '.'));
      if (!amount || amount <= 0) {
        this.$notify(this.$t('stripe_payment.amount_error'), '', 'warning', 4000);
        return;
      }

      this.isLoading = true;
      this.errorMessage = null;
      this.paymentStatus = 'pending';

      const payload = {
        booking_id: this.bookingId,
        last_name: this.lastName,
        client_id: this.client.id,
        amount: amount.toFixed(2),
      };

      if (this.paymentDescription) {
        payload.description = this.paymentDescription;
      }

      this.axios
        .post('/api/v1/integrations/public_stripe_payments/orders', payload)
        .then((response) => {
          if (!response.data?.payment_url) {
            this.paymentStatus = null;
            this.errorMessage = this.$t('stripe_payment.no_payment_url');
            return;
          }
          
          this.paymentUrl = response.data.payment_url;
          this.paymentId = response.data.payment_id;
          this.orderId = response.data.order_id;
          
          this.generateQrCode(this.paymentUrl);
          this.$notify(
            this.$t('stripe_payment.link_ready_title'),
            this.$t('stripe_payment.link_ready_text'),
            'info',
            5000
          );
        })
        .catch((error) => {
          this.paymentStatus = null;
          this.errorMessage = error.response?.data?.error || error.message || this.$t('stripe_payment.status_error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    generateQrCode(url) {
      QRCode.toDataURL(url, { width: 250, margin: 2 })
        .then(dataUrl => {
          this.qrCode = dataUrl;
        })
        .catch(err => {
          console.error('QR code generation error:', err);
        });
    },
    
    handlePaymentUpdate(data) {
      if (!data?.stripe_payment) return;
      
      const payment = data.stripe_payment;
      if (this.paymentId && payment.id !== this.paymentId) return;
      
      this.paymentStatus = payment.status;
      
      if (payment.status === 'success') {
        this.$notify(this.$t('stripe_payment.success_message'), '', 'success', 4000);
        this.$emit('payments-updated');
        this.closeDialog();
      } else if (payment.status === 'failed') {
        this.errorMessage = this.$t('stripe_payment.failed_message');
      } else if (payment.status === 'cancelled') {
        this.errorMessage = this.$t('stripe_payment.cancelled_message');
      }
    },
    
    async copyToClipboard(value) {
      if (!value) return;
      
      try {
        await copyTextToClipboard(value);
        this.$notify(this.$t('stripe_payment.copy_success'), '', 'success', 3000);
      } catch (error) {
        this.$notify(this.$t('stripe_payment.copy_failed'), '', 'error', 3000);
      }
    },
    
    openExternalLink(url) {
      if (url) window.open(url, '_blank');
    },
  },
};
</script>

